import React, { useState, useCallback, useEffect, useMemo } from 'react';

type CustomTheme = {
  id: string;
  name: string;
  base: 'dark' | 'light';
  colors: {
    bg: string;
    bgSecondary: string;
    text: string;
    textSecondary: string;
    border: string;
    accent: string;
    success: string;
    warning: string;
    error: string;
    muted: string;
  };
};

const STORAGE_CUSTOM_THEMES = 'atlas-ide:v2:custom-themes';
const STORAGE_ACTIVE_CUSTOM_THEME = 'atlas-ide:v2:active-custom-theme';

const defaultDarkColors: CustomTheme['colors'] = {
  bg: '#1e1e2e',
  bgSecondary: '#2a2a3a',
  text: '#e0e0e0',
  textSecondary: '#a0a0a0',
  border: '#3a3a4a',
  accent: '#7c3aed',
  success: '#22c55e',
  warning: '#f59e0b',
  error: '#ef4444',
  muted: '#6b7280',
};

const defaultLightColors: CustomTheme['colors'] = {
  bg: '#ffffff',
  bgSecondary: '#f5f5f5',
  text: '#1f2937',
  textSecondary: '#4b5563',
  border: '#e5e7eb',
  accent: '#7c3aed',
  success: '#16a34a',
  warning: '#d97706',
  error: '#dc2626',
  muted: '#9ca3af',
};

type CustomThemeModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onApplyTheme: (theme: CustomTheme | null) => void;
  currentTheme: 'dark' | 'light';
};

export const CustomThemeModal = React.memo(function CustomThemeModal({
  isOpen,
  onClose,
  onApplyTheme,
  currentTheme,
}: CustomThemeModalProps) {
  const [themes, setThemes] = useState<CustomTheme[]>([]);
  const [selectedThemeId, setSelectedThemeId] = useState<string | null>(null);
  const [editingTheme, setEditingTheme] = useState<CustomTheme | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  // Load saved themes
  useEffect(() => {
    if (!isOpen) return;

    try {
      const saved = localStorage.getItem(STORAGE_CUSTOM_THEMES);
      if (saved) {
        setThemes(JSON.parse(saved));
      }
      const activeId = localStorage.getItem(STORAGE_ACTIVE_CUSTOM_THEME);
      if (activeId) {
        setSelectedThemeId(activeId);
      }
    } catch {
      // Ignore errors
    }
  }, [isOpen]);

  // Save themes
  const saveThemes = useCallback((newThemes: CustomTheme[]) => {
    setThemes(newThemes);
    localStorage.setItem(STORAGE_CUSTOM_THEMES, JSON.stringify(newThemes));
  }, []);

  // Create new theme
  const handleCreateTheme = useCallback(() => {
    const baseColors = currentTheme === 'dark' ? defaultDarkColors : defaultLightColors;
    const newTheme: CustomTheme = {
      id: `custom-${Date.now()}`,
      name: `Custom Theme ${themes.length + 1}`,
      base: currentTheme,
      colors: { ...baseColors },
    };
    setEditingTheme(newTheme);
    setIsCreating(true);
  }, [currentTheme, themes.length]);

  // Edit existing theme
  const handleEditTheme = useCallback((theme: CustomTheme) => {
    setEditingTheme({ ...theme, colors: { ...theme.colors } });
    setIsCreating(false);
  }, []);

  // Save edited theme
  const handleSaveTheme = useCallback(() => {
    if (!editingTheme) return;

    const newThemes = isCreating
      ? [...themes, editingTheme]
      : themes.map(t => t.id === editingTheme.id ? editingTheme : t);

    saveThemes(newThemes);
    setEditingTheme(null);
    setIsCreating(false);
  }, [editingTheme, isCreating, themes, saveThemes]);

  // Delete theme
  const handleDeleteTheme = useCallback((themeId: string) => {
    const newThemes = themes.filter(t => t.id !== themeId);
    saveThemes(newThemes);
    if (selectedThemeId === themeId) {
      setSelectedThemeId(null);
      localStorage.removeItem(STORAGE_ACTIVE_CUSTOM_THEME);
      onApplyTheme(null);
    }
  }, [themes, selectedThemeId, saveThemes, onApplyTheme]);

  // Apply theme
  const handleApplyTheme = useCallback((theme: CustomTheme) => {
    setSelectedThemeId(theme.id);
    localStorage.setItem(STORAGE_ACTIVE_CUSTOM_THEME, theme.id);
    onApplyTheme(theme);
  }, [onApplyTheme]);

  // Reset to default
  const handleResetToDefault = useCallback(() => {
    setSelectedThemeId(null);
    localStorage.removeItem(STORAGE_ACTIVE_CUSTOM_THEME);
    onApplyTheme(null);
  }, [onApplyTheme]);

  // Update color
  const updateColor = useCallback((key: keyof CustomTheme['colors'], value: string) => {
    if (!editingTheme) return;
    setEditingTheme({
      ...editingTheme,
      colors: { ...editingTheme.colors, [key]: value },
    });
  }, [editingTheme]);

  // Update theme name
  const updateThemeName = useCallback((name: string) => {
    if (!editingTheme) return;
    setEditingTheme({ ...editingTheme, name });
  }, [editingTheme]);

  const handleClose = useCallback(() => {
    setEditingTheme(null);
    setIsCreating(false);
    onClose();
  }, [onClose]);

  // Color labels
  const colorLabels: Record<keyof CustomTheme['colors'], string> = {
    bg: 'Background',
    bgSecondary: 'Secondary Background',
    text: 'Text',
    textSecondary: 'Secondary Text',
    border: 'Border',
    accent: 'Accent',
    success: 'Success',
    warning: 'Warning',
    error: 'Error',
    muted: 'Muted',
  };

  if (!isOpen) return null;

  return (
    <div className="modal-backdrop" onClick={handleClose}>
      <div
        className="modal custom-theme-modal"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: '600px', maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}
      >
        <div className="modal-header">
          <div className="modal-title">
            {editingTheme ? (isCreating ? 'Create Theme' : 'Edit Theme') : 'Custom Themes'}
          </div>
          <button className="modal-close" onClick={handleClose}>
            x
          </button>
        </div>

        <div className="modal-body" style={{ padding: '16px', overflow: 'auto', flex: 1 }}>
          {editingTheme ? (
            // Theme Editor
            <>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: 'var(--muted)' }}>
                  Theme Name:
                </label>
                <input
                  type="text"
                  value={editingTheme.name}
                  onChange={(e) => updateThemeName(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px',
                    background: 'var(--bg-secondary)',
                    border: '1px solid var(--border)',
                    borderRadius: '4px',
                    color: 'var(--text)',
                    fontSize: '13px',
                  }}
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: 'var(--muted)' }}>
                  Base Theme:
                </label>
                <select
                  value={editingTheme.base}
                  onChange={(e) => setEditingTheme({ ...editingTheme, base: e.target.value as 'dark' | 'light' })}
                  style={{
                    padding: '8px',
                    background: 'var(--bg-secondary)',
                    border: '1px solid var(--border)',
                    borderRadius: '4px',
                    color: 'var(--text)',
                    fontSize: '13px',
                  }}
                >
                  <option value="dark">Dark</option>
                  <option value="light">Light</option>
                </select>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                {(Object.keys(editingTheme.colors) as (keyof CustomTheme['colors'])[]).map((key) => (
                  <div key={key}>
                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: 'var(--muted)' }}>
                      {colorLabels[key]}:
                    </label>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <input
                        type="color"
                        value={editingTheme.colors[key]}
                        onChange={(e) => updateColor(key, e.target.value)}
                        style={{
                          width: '40px',
                          height: '32px',
                          padding: '0',
                          border: '1px solid var(--border)',
                          borderRadius: '4px',
                          cursor: 'pointer',
                        }}
                      />
                      <input
                        type="text"
                        value={editingTheme.colors[key]}
                        onChange={(e) => updateColor(key, e.target.value)}
                        style={{
                          flex: 1,
                          padding: '6px 8px',
                          background: 'var(--bg-secondary)',
                          border: '1px solid var(--border)',
                          borderRadius: '4px',
                          color: 'var(--text)',
                          fontSize: '12px',
                          fontFamily: 'monospace',
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>

              {/* Preview */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: 'var(--muted)' }}>
                  Preview:
                </label>
                <div
                  style={{
                    padding: '16px',
                    borderRadius: '4px',
                    background: editingTheme.colors.bg,
                    border: `1px solid ${editingTheme.colors.border}`,
                  }}
                >
                  <div style={{ color: editingTheme.colors.text, marginBottom: '8px' }}>Primary Text</div>
                  <div style={{ color: editingTheme.colors.textSecondary, marginBottom: '8px', fontSize: '12px' }}>Secondary Text</div>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <span style={{ padding: '4px 8px', background: editingTheme.colors.accent, color: '#fff', borderRadius: '4px', fontSize: '12px' }}>Accent</span>
                    <span style={{ padding: '4px 8px', background: editingTheme.colors.success, color: '#fff', borderRadius: '4px', fontSize: '12px' }}>Success</span>
                    <span style={{ padding: '4px 8px', background: editingTheme.colors.warning, color: '#fff', borderRadius: '4px', fontSize: '12px' }}>Warning</span>
                    <span style={{ padding: '4px 8px', background: editingTheme.colors.error, color: '#fff', borderRadius: '4px', fontSize: '12px' }}>Error</span>
                  </div>
                  <div
                    style={{
                      marginTop: '8px',
                      padding: '8px',
                      background: editingTheme.colors.bgSecondary,
                      border: `1px solid ${editingTheme.colors.border}`,
                      borderRadius: '4px',
                      color: editingTheme.colors.muted,
                      fontSize: '12px',
                    }}
                  >
                    Secondary background with muted text
                  </div>
                </div>
              </div>
            </>
          ) : (
            // Theme List
            <>
              <div style={{ marginBottom: '16px' }}>
                <button
                  className="pill-btn"
                  onClick={handleCreateTheme}
                  style={{ width: '100%', padding: '12px' }}
                >
                  + Create New Theme
                </button>
              </div>

              {themes.length === 0 ? (
                <div style={{
                  textAlign: 'center',
                  color: 'var(--muted)',
                  padding: '40px 20px',
                  fontSize: '13px',
                }}>
                  No custom themes yet. Create one to get started!
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {themes.map((theme) => (
                    <div
                      key={theme.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '12px',
                        background: selectedThemeId === theme.id ? 'var(--accent)' : 'var(--bg-secondary)',
                        color: selectedThemeId === theme.id ? '#fff' : undefined,
                        borderRadius: '4px',
                        cursor: 'pointer',
                      }}
                      onClick={() => handleApplyTheme(theme)}
                    >
                      {/* Color preview */}
                      <div style={{ display: 'flex', gap: '2px' }}>
                        {['bg', 'accent', 'success', 'error'].map((colorKey) => (
                          <div
                            key={colorKey}
                            style={{
                              width: '16px',
                              height: '16px',
                              background: theme.colors[colorKey as keyof CustomTheme['colors']],
                              borderRadius: '2px',
                              border: '1px solid var(--border)',
                            }}
                          />
                        ))}
                      </div>

                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 'bold', fontSize: '13px' }}>{theme.name}</div>
                        <div style={{ fontSize: '11px', opacity: 0.7 }}>
                          {theme.base === 'dark' ? 'Dark' : 'Light'} base
                        </div>
                      </div>

                      <div style={{ display: 'flex', gap: '4px' }}>
                        <button
                          className="pill-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditTheme(theme);
                          }}
                          style={{ fontSize: '11px', padding: '4px 8px' }}
                        >
                          Edit
                        </button>
                        <button
                          className="pill-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteTheme(theme.id);
                          }}
                          style={{ fontSize: '11px', padding: '4px 8px', color: 'var(--error)' }}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {selectedThemeId && (
                <div style={{ marginTop: '16px' }}>
                  <button
                    className="pill-btn"
                    onClick={handleResetToDefault}
                    style={{ width: '100%' }}
                  >
                    Reset to Default Theme
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        <div className="modal-footer">
          {editingTheme ? (
            <>
              <button className="pill-btn" onClick={() => setEditingTheme(null)}>
                Cancel
              </button>
              <button
                className="pill-btn"
                onClick={handleSaveTheme}
                style={{ background: 'var(--accent)', color: 'white' }}
              >
                Save Theme
              </button>
            </>
          ) : (
            <button className="pill-btn" onClick={handleClose}>
              Close
            </button>
          )}
        </div>
      </div>
    </div>
  );
});

export default CustomThemeModal;
